import React, { ReactNode } from 'react'
import { Table, Pagination, Menu, ActionIcon, Box, Skeleton, Stack, Group, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { FiMoreVertical } from 'react-icons/fi'
import AppCard from './AppCard'
import EmptyState from './EmptyState'

export interface ColumnDef<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  sortable?: boolean;
}

export interface RowAction<T> {
  label: string;
  onClick: (row: T) => void;
  color?: string;
}

interface Props<T> {
  columns: ColumnDef<T>[];
  data: T[];
  pagination: { totalCount: number; pageNumber: number; pageSize: number };
  onPageChange: (page: number) => void;
  loading?: boolean;
  rowActions?: RowAction<T>[];
}

function getRowKey<T> (row: T, columns: ColumnDef<T>[]): string {
  const keyCol = columns[0]
  if (!keyCol) return String(Math.random())
  const val = (row as Record<string, unknown>)[keyCol.key]
  return val != null ? String(val) : String(Math.random())
}

function renderCell<T> (row: T, col: ColumnDef<T>): ReactNode {
  return col.render
    ? col.render(row)
    : String((row as Record<string, unknown>)[col.key] ?? '')
}

function RowActionsMenu<T> ({
  row,
  rowActions
}: {
  row: T;
  rowActions: RowAction<T>[];
}) {
  return (
    <Menu shadow="md" width={180} position="bottom-end">
      <Menu.Target>
        <ActionIcon variant="subtle" size="sm">
          <FiMoreVertical size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {rowActions.map((action, idx) => (
          <Menu.Item
            key={idx}
            color={action.color}
            onClick={() => action.onClick(row)}
          >
            {action.label}
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  )
}

function TableSkeleton<T> ({
  columns,
  rowActions
}: {
  columns: ColumnDef<T>[];
  rowActions?: RowAction<T>[];
}) {
  return (
    <Box
      sx={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        minWidth: 0,
        marginLeft: -4,
        marginRight: -4
      }}
    >
      <Table striped highlightOnHover sx={{ minWidth: 'max-content' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            {rowActions && rowActions.length > 0 && <th style={{ width: 48 }} />}
          </tr>
        </thead>
        <tbody>
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              {columns.map((col) => (
                <td key={col.key}>
                  <Skeleton height={20} />
                </td>
              ))}
              {rowActions && rowActions.length > 0 && (
                <td>
                  <Skeleton height={24} width={24} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  )
}

function CardSkeleton<T> ({
  columns,
  rowActions
}: {
  columns: ColumnDef<T>[];
  rowActions?: RowAction<T>[];
}) {
  const detailCount = Math.max(0, columns.length - 1)

  return (
    <Stack spacing="sm">
      {[1, 2, 3, 4, 5].map((i) => (
        <AppCard key={i} padding="sm" withBorder>
          <Group position="apart" align="flex-start" noWrap>
            <Skeleton height={20} width="60%" />
            {rowActions && rowActions.length > 0 && <Skeleton height={24} width={24} />}
          </Group>
          <Stack spacing={6} mt="xs">
            {Array.from({ length: detailCount }, (_, j) => (
              <Group key={j} position="apart" noWrap>
                <Skeleton height={12} width={64} />
                <Skeleton height={16} width="40%" />
              </Group>
            ))}
          </Stack>
        </AppCard>
      ))}
    </Stack>
  )
}

function DesktopTable<T> ({
  columns,
  data,
  rowActions
}: {
  columns: ColumnDef<T>[];
  data: T[];
  rowActions?: RowAction<T>[];
}) {
  return (
    <Box
      sx={{
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        minWidth: 0,
        marginLeft: -4,
        marginRight: -4
      }}
    >
      <Table striped highlightOnHover sx={{ minWidth: 'max-content' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
            {rowActions && rowActions.length > 0 && <th style={{ width: 48 }} />}
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={getRowKey(row, columns)}>
              {columns.map((col) => (
                <td key={col.key}>{renderCell(row, col)}</td>
              ))}
              {rowActions && rowActions.length > 0 && (
                <td>
                  <RowActionsMenu row={row} rowActions={rowActions} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </Box>
  )
}

function MobileCardList<T> ({
  columns,
  data,
  rowActions
}: {
  columns: ColumnDef<T>[];
  data: T[];
  rowActions?: RowAction<T>[];
}) {
  const [primaryCol, ...detailCols] = columns

  return (
    <Stack spacing="sm">
      {data.map((row) => (
        <AppCard key={getRowKey(row, columns)} padding="sm" withBorder>
          {primaryCol && (
            <Group position="apart" align="flex-start" noWrap spacing="xs">
              <Box sx={{ minWidth: 0, flex: 1, fontWeight: 500 }}>
                {renderCell(row, primaryCol)}
              </Box>
              {rowActions && rowActions.length > 0 && (
                <Box sx={{ flexShrink: 0 }}>
                  <RowActionsMenu row={row} rowActions={rowActions} />
                </Box>
              )}
            </Group>
          )}
          {detailCols.length > 0 && (
            <Stack spacing={4} mt={primaryCol ? 'xs' : 0}>
              {detailCols.map((col) => (
                <Group key={col.key} position="apart" spacing="xs" noWrap align="flex-start">
                  <Text size="xs" color="dimmed" sx={{ flexShrink: 0 }}>
                    {col.label}
                  </Text>
                  <Box sx={{ textAlign: 'right', minWidth: 0, flex: 1 }}>
                    {renderCell(row, col)}
                  </Box>
                </Group>
              ))}
            </Stack>
          )}
        </AppCard>
      ))}
    </Stack>
  )
}

export default function AdminTable<T> ({
  columns,
  data,
  pagination,
  onPageChange,
  loading = false,
  rowActions
}: Props<T>) {
  const mobile = useMediaQuery('(max-width: 768px)')
  const { totalCount, pageNumber, pageSize } = pagination
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  if (loading) {
    return mobile
      ? <CardSkeleton columns={columns} rowActions={rowActions} />
      : <TableSkeleton columns={columns} rowActions={rowActions} />
  }

  if (data.length === 0) {
    return (
      <EmptyState
        title="No results"
        message="Nothing to display."
      />
    )
  }

  return (
    <Box>
      {mobile
        ? <MobileCardList columns={columns} data={data} rowActions={rowActions} />
        : <DesktopTable columns={columns} data={data} rowActions={rowActions} />}
      {totalPages > 1 && (
        <Box sx={mobile ? { display: 'flex', justifyContent: 'center' } : undefined}>
          <Pagination
            total={totalPages}
            page={pageNumber}
            onChange={onPageChange}
            position="right"
            mt="md"
          />
        </Box>
      )}
    </Box>
  )
}
